import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import facilityService from '../services/facilityService';
import clientService from '../services/clientService';
import { FacilityDTO } from '../types/facility.types';
import { ClientDTO } from '../types/client.types';

const ClientFacilitiesPage: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const [facilities, setFacilities] = useState<FacilityDTO[]>([]);
  const [client, setClient] = useState<ClientDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');
  const [currentFacility, setCurrentFacility] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: '',
    subdomain: '',
    adminEmail: '',
    status: 'Active',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Fetch facilities and client info on component mount
  useEffect(() => {
    if (clientId) {
      fetchClientAndFacilities();
    }
  }, [clientId]);

  const fetchClientAndFacilities = async () => {
    try {
      setLoading(true);
      // Fetch client info
      const clientData = await clientService.getClientById(clientId!);
      setClient(clientData);

      // Fetch facilities for this client
      const facilitiesData = await facilityService.getFacilitiesByClient(clientId!);
      setFacilities(facilitiesData);
      setErrorMessage('');
    } catch (error: any) {
      console.error('Error fetching data:', error);
      setErrorMessage(error.message || 'Failed to load facilities');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Facility name is required';
    }

    if (!formData.subdomain.trim()) {
      newErrors.subdomain = 'Subdomain is required';
    } else if (!/^[a-z0-9-]+$/.test(formData.subdomain)) {
      newErrors.subdomain = 'Subdomain can only contain lowercase letters, numbers, and hyphens';
    }

    if (!formData.adminEmail.trim()) {
      newErrors.adminEmail = 'Admin email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail)) {
      newErrors.adminEmail = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddFacility = () => {
    setDialogMode('add');
    setFormData({
      name: '',
      subdomain: '',
      adminEmail: '',
      status: 'Active',
    });
    setCurrentFacility(null);
    setErrors({});
    setSuccessMessage('');
    setOpenDialog(true);
  };

  const handleEditFacility = (facility: any) => {
    setDialogMode('edit');
    setFormData({
      name: facility.name,
      subdomain: facility.subdomain,
      adminEmail: facility.adminEmail,
      status: facility.status,
    });
    setCurrentFacility(facility);
    setErrors({});
    setSuccessMessage('');
    setOpenDialog(true);
  };

  const handleDeleteFacility = async (id: string) => {
    if (window.confirm(`Are you sure you want to delete this facility? `)) {
      try {
        await facilityService.deleteFacility(id);
        setFacilities(facilities.filter(f => f.id !== id));
        setSuccessMessage('Facility deleted successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (error: any) {
        setErrorMessage(error.message || 'Failed to delete facility');
        setTimeout(() => setErrorMessage(''), 3000);
      }
    }
  };

  const handleViewFacility = (facility: FacilityDTO) => {
    // In a real app, this would navigate to the facility dashboard
    alert(`Navigating to ${facility.name} dashboard`);
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      if (dialogMode === 'add') {
        const newFacility = await facilityService.createFacility({
          name: formData.name,
          code: formData.subdomain,
          address: '',
          city: '',
          state: '',
          country: 'USA',
          postalCode: '',
          phone: '000-000-0000',
          email: formData.adminEmail,
          clientId: clientId!,
          admissionType: 'BOTH' as any,
        });

        setFacilities([...facilities, newFacility]);
        setSuccessMessage('Facility added successfully');
      } else if (currentFacility) {
        const updatedFacility = await facilityService.updateFacility(currentFacility.id, {
          name: formData.name,
          email: formData.adminEmail,
          isActive: formData.status === 'Active',
        });

        const updatedFacilities = facilities.map(f =>
          f.id === currentFacility.id ? updatedFacility : f
        );

        setFacilities(updatedFacilities);
        setSuccessMessage('Facility updated successfully');
      }

      setOpenDialog(false);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error: any) {
      setErrorMessage(error.message || 'Failed to save facility');
      setTimeout(() => setErrorMessage(''), 3000);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Facilities for {client?.name || `Client #${clientId}`}
      </Typography>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      <Paper sx={{ p: 2, mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}>
        <Typography variant="h6">Manage Healthcare Facilities</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddFacility}
          sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
        >
          Add New Facility
        </Button>
      </Paper>

      {!loading && (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 1000 }}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {facilities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No facilities found
                  </TableCell>
                </TableRow>
              ) : (
                facilities.map((facility) => (
                  <TableRow key={facility.id}>
                    <TableCell>{facility.name}</TableCell>
                    <TableCell>{facility.code || 'N/A'}</TableCell>
                    <TableCell>{facility.email}</TableCell>
                    <TableCell>{facility.phone}</TableCell>
                    <TableCell>{facility.address || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip
                        label={facility.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        color={facility.isActive ? 'success' : 'warning'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        onClick={() => handleViewFacility(facility)}
                        sx={{ mr: 1 }}
                        title="View Facility"
                      >
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton
                        color="primary"
                        onClick={() => handleEditFacility(facility)}
                        sx={{ mr: 1 }}
                        title="Edit Facility"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => facility.id && handleDeleteFacility(facility.id)}
                        title="Delete Facility"
                        disabled={!facility.id}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Facility Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>
          {dialogMode === 'add' ? 'Add New Facility' : 'Edit Facility'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {dialogMode === 'add'
              ? 'Enter the details for the new healthcare facility.'
              : 'Update the details for this healthcare facility.'}
          </DialogContentText>

          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Facility Name"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
            sx={{ mb: 2 }}
          />

          <TextField
            margin="dense"
            name="subdomain"
            label="Subdomain"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.subdomain}
            onChange={handleChange}
            error={!!errors.subdomain}
            helperText={errors.subdomain || "This will be used as the subdomain (e.g., 'facility.clientname.com')"}
            disabled={dialogMode === 'edit'} // Prevent changing subdomain after creation
            sx={{ mb: 2 }}
          />

          <TextField
            margin="dense"
            name="adminEmail"
            label="Admin Contact Email"
            type="email"
            fullWidth
            variant="outlined"
            value={formData.adminEmail}
            onChange={handleChange}
            error={!!errors.adminEmail}
            helperText={errors.adminEmail}
            sx={{ mb: 2 }}
          />

          <FormControl fullWidth margin="dense">
            <InputLabel id="status-select-label">Status</InputLabel>
            <Select
              labelId="status-select-label"
              id="status-select"
              name="status"
              value={formData.status}
              label="Status"
              onChange={(e: SelectChangeEvent<string>) => setFormData({ ...formData, status: e.target.value as string })}
            >
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="In Progress">In Progress</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {dialogMode === 'add' ? 'Add Facility' : 'Update Facility'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientFacilitiesPage;